"use client";

import type { ReactNode } from "react";
import { Info } from "lucide-react";

import type { Dictionary } from "@/dictionaries/types";
import { useCollectionAnalytics } from "@/features/collections/hooks/useCollectionAnalytics";
import { SecGlyph } from "@/features/home-v2/ornament";
import { tokenAmount } from "../parts";

type Labels = Dictionary["collectionPage"];

function Missing({ reason }: { reason: string }) {
  return (
    <span className="vt-na v2-tip" data-tip={reason} tabIndex={0}>
      <span aria-hidden>—</span><Info aria-hidden /><span className="v2-sr-only">{reason}</span>
    </span>
  );
}

/**
 * THE FIGURES AS ONE BAND — items from the server's total; volume, sales and
 * average from the analytics endpoint (one request, shared with the Analytics
 * tab); the lowest and highest price of the collection's works from the
 * collection itself (`floor_price` / `max_price`, replacing the floor and
 * owners cells that had no source). Read left to right as one ledger line; on
 * a phone it is a swipeable rail that snaps per figure.
 */
export default function StatsBand({ contract, lang, currency, itemsTotal, priceRange, t }: {
  contract: string; lang: "fa" | "en"; currency: string; itemsTotal: number | null;
  priceRange: { min: number | null; max: number | null }; t: Labels;
}) {
  const locale = lang === "fa" ? "fa-IR" : "en-US";
  const nf = new Intl.NumberFormat(locale);
  const { analytics, isLoading, isSuccess } = useCollectionAnalytics(contract, lang);
  const reason = !isLoading && !isSuccess ? t.metricRequestFailed : t.metricUnavailable;
  const stats = analytics?.stats;
  const finite = (n: unknown) => n != null && Number.isFinite(Number(n));
  const money = (n: number | null | undefined): ReactNode =>
    isLoading ? <span className="vt-pending" /> : finite(n) ? <>{tokenAmount(Number(n), locale)}<small>{currency}</small></> : <Missing reason={reason} />;
  // The price range arrives with the page, so it is never pending.
  const price = (n: number | null): ReactNode =>
    n == null ? <Missing reason={t.priceRangeUnavailable} /> : <>{tokenAmount(n, locale)}<small>{currency}</small></>;

  const cells: { key: string; label: string; value: ReactNode; lead?: boolean }[] = [
    { key: "items", label: t.items, value: itemsTotal == null ? <Missing reason={t.itemsCountUnavailable} /> : nf.format(itemsTotal), lead: true },
    { key: "volume", label: t.volume, value: money(stats?.volume), lead: true },
    { key: "sales", label: t.sales, value: isLoading ? <span className="vt-pending" /> : finite(stats?.sales) ? nf.format(Number(stats!.sales)) : <Missing reason={reason} /> },
    { key: "average", label: t.averagePrice, value: money(stats?.average_price) },
    { key: "min", label: t.minPrice, value: price(priceRange.min) },
    { key: "max", label: t.maxPrice, value: price(priceRange.max) },
  ];

  return (
    <section className="vt-stats" aria-labelledby="vt-stats-h">
      <h2 id="vt-stats-h" className="v2-sr-only">{t.statsHeading}</h2>
      <dl className="vt-stats-row">
        {cells.map((c) => (
          <div key={c.key} className="vt-stat" data-lead={c.lead || undefined}>
            <dt>{c.label}</dt>
            <dd>{c.value}</dd>
          </div>
        ))}
      </dl>
      <div className="vt-divider" aria-hidden><SecGlyph /></div>
    </section>
  );
}
